import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scissors, ArrowLeft, CaretLeft, CaretRight, Wallet,
  Plus, Money, CreditCard, ArrowDown, ArrowUp,
  Lock, X, FloppyDisk, Spinner,
  Trash, ClockCounterClockwise, LockOpen, WarningCircle,
  CurrencyDollar, Receipt, PixLogo, DotsThree,
  FileXls, FilePdf, Bank, HandCoins, Storefront, Minus,
  ShieldCheck,
} from '@phosphor-icons/react';
import {
  API, TIPO_CONFIG, FORMA_CONFIG, BANDEIRAS, FORMAS_CARTAO, ORIGEM_CONFIG, STATUS_CAIXA,
  formatCurrency, formatDateBR, isToday, formatTime,
} from './constants';
import { HistoricoDiaPanel, useHistoricoDoDia } from './HistoricoDiaPanel';
import { acaoComandaEstorno } from '@/lib/comandaAcoes';

// ==================== RETROATIVO AUTH MODAL ====================
// Uses real user authentication (ADMIN profile required).
const RetroativoAuthModal = ({ titulo, descricao, selectedDate, onClose, onAuthorized }) => {
  const [step, setStep] = useState('auth'); // auth | justificativa
  const [loginStr, setLoginStr] = useState('');
  const [password, setPassword] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [adminUser, setAdminUser] = useState(null);

  const handleValidate = async () => {
    if (!loginStr.trim() || !password.trim()) { setError('Preencha login e senha'); return; }
    setValidating(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/validate-admin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginStr, password }),
      });
      if (!res.ok) {
        const err = await res.json();
        setError(err?.detail || 'Credenciais invalidas');
        return;
      }
      const data = await res.json();
      setAdminUser(data);
      setStep('justificativa');
    } catch { setError('Erro ao validar'); }
    finally { setValidating(false); }
  };

  const handleConfirm = () => {
    if (justificativa.trim().length < 5) { setError('Justificativa deve ter pelo menos 5 caracteres'); return; }
    // Pass both admin credentials and user info for audit
    onAuthorized(loginStr, password, justificativa, adminUser);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="retroativo-auth-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 pb-4 border-b" style={{ borderColor: '#C9A96230' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#C9A96220' }}>
              <ShieldCheck size={22} weight="fill" style={{ color: '#C9A962' }} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{titulo}</h2>
              <p className="text-[11px] text-[#71717A]">Autorizacao gerencial necessaria</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed" style={{ borderColor: '#C9A96240', backgroundColor: '#C9A96208' }}>
            <ClockCounterClockwise size={14} weight="bold" style={{ color: '#C9A962' }} />
            <span className="text-xs font-medium" style={{ color: '#8B7340' }}>
              {descricao} — {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>

        {step === 'auth' ? (
          <div className="p-6 space-y-4">
            <div className="text-center mb-2">
              <Lock size={32} weight="light" className="mx-auto mb-2" style={{ color: '#C9A962' }} />
              <p className="text-sm text-[#71717A]">Apenas usuarios ADMIN podem autorizar operacoes retroativas.</p>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Login do Autorizador</label>
              <input data-testid="input-retro-login" type="text" value={loginStr}
                onChange={(e) => { setLoginStr(e.target.value); setError(''); }}
                placeholder="Login" autoFocus autoComplete="username"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30" />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Senha</label>
              <input data-testid="input-retro-password" type="password" value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
                placeholder="Senha do autorizador" autoComplete="current-password"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A962]/30" />
            </div>
            {error && (
              <div className="flex items-center gap-1.5 text-red-500">
                <WarningCircle size={14} weight="fill" />
                <span className="text-xs font-medium">{error}</span>
              </div>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">Cancelar</button>
              <button data-testid="btn-validar-admin" onClick={handleValidate} disabled={validating || !loginStr.trim() || !password.trim()}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#C9A962' }}>
                {validating ? <Spinner size={16} className="animate-spin" /> : <ShieldCheck size={16} weight="bold" />}
                {validating ? 'Validando...' : 'Autorizar'}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {adminUser && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F0FDF4] border border-[#1B433220]">
                <ShieldCheck size={14} weight="fill" style={{ color: '#1B4332' }} />
                <span className="text-xs font-medium text-[#1B4332]">Autorizado por: {adminUser.user_name} ({adminUser.user_role})</span>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide mb-1" style={{ color: '#C9A962' }}>Justificativa *</label>
              <textarea data-testid="input-justificativa" value={justificativa}
                onChange={(e) => { setJustificativa(e.target.value); setError(''); }}
                placeholder="Descreva o motivo do ajuste retroativo..." rows={3} autoFocus
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border rounded-xl text-sm focus:outline-none focus:ring-2 resize-none"
                style={{ borderColor: '#C9A96240' }} />
              {error && (
                <div className="flex items-center gap-1.5 mt-2 text-red-500">
                  <WarningCircle size={14} weight="fill" />
                  <span className="text-xs font-medium">{error}</span>
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A]">Cancelar</button>
              <button data-testid="btn-confirmar-retroativo" onClick={handleConfirm}
                disabled={justificativa.trim().length < 5}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: '#C9A962' }}>
                <ShieldCheck size={16} weight="bold" />Confirmar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ABRIR CAIXA MODAL (always R$0) ====================
const AbrirCaixaModal = ({ selectedDate, onClose, onSuccess }) => {
  const [responsavel, setResponsavel] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/caixa/abrir`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: selectedDate, responsavel, observacoes }),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err?.detail?.message || err?.detail || 'Erro ao abrir caixa');
        return;
      }
      onSuccess();
    } catch { /* */ } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="abrir-caixa-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
              <LockOpen size={20} weight="bold" style={{ color: '#1B4332' }} />
            </div>
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Abrir Caixa</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Fixed R$0 opening */}
          <div className="p-3 rounded-xl bg-[#F7F6F4] border border-[#E4E4E7]">
            <p className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide mb-1">Saldo Inicial</p>
            <p className="text-2xl font-bold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>R$ 0,00</p>
            <p className="text-[11px] text-[#A1A1AA] mt-1">O caixa sempre abre zerado. Dinheiro do dia anterior vai para o cofre.</p>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Responsavel</label>
            <input data-testid="input-responsavel" type="text" value={responsavel}
              onChange={(e) => setResponsavel(e.target.value)} placeholder="Nome do responsavel" autoFocus
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Observacoes</label>
            <input data-testid="input-obs-abertura" type="text" value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)} placeholder="Opcional"
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="btn-confirmar-abertura"
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:shadow-lg"
              style={{ backgroundColor: '#1B4332' }}>
              {saving ? <Spinner size={16} className="animate-spin" /> : <LockOpen size={16} weight="bold" />}
              {saving ? 'Abrindo...' : 'Abrir Caixa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== MOVIMENTACAO MODAL ====================
const MovimentacaoModal = ({ tipo, selectedDate, onClose, onSuccess }) => {
  const cfg = TIPO_CONFIG[tipo];
  const showForma = tipo === 'entrada';
  const showOrigem = tipo === 'saida' || tipo === 'suprimento';

  const [valor, setValor] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('dinheiro');
  const [origem, setOrigem] = useState('caixa_fisico');
  const [descricao, setDescricao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = parseFloat(valor.replace(',', '.')) || 0;
    if (v <= 0) return;
    setSaving(true);
    try {
      // Check if this is a retroactive operation (credentials stored temporarily)
      const retroAuth = window._retroAuth;
      let url = `${API}/caixa/movimentacao`;
      let body = { data: selectedDate, tipo, valor: v, forma_pagamento: formaPagamento, origem, descricao, observacoes };

      if (retroAuth) {
        url = `${API}/caixa/movimentacao/retroativo`;
        body = { ...body, admin_login: retroAuth.admin_login, admin_password: retroAuth.admin_password, justificativa: retroAuth.justificativa };
        window._retroAuth = null;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err?.detail?.message || err?.detail || 'Erro ao registrar');
        return;
      }
      onSuccess();
    } catch { /* */ } finally { setSaving(false); }
  };

  const origemIcons = {
    caixa_fisico: Storefront,
    caixinha_cora: Bank,
    aporte_pessoal: HandCoins,
    outro: DotsThree,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="movimentacao-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: cfg.bg }}>
              {tipo === 'entrada' || tipo === 'suprimento' ?
                <ArrowDown size={20} weight="bold" style={{ color: cfg.color }} /> :
                <ArrowUp size={20} weight="bold" style={{ color: cfg.color }} />}
            </div>
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{cfg.label}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Valor (R$) *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium" style={{ color: cfg.color }}>R$</span>
              <input data-testid="input-valor" type="text" value={valor}
                onChange={(e) => setValor(e.target.value)} placeholder="0,00" autoFocus
                className="w-full pl-10 pr-3 py-3 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20 text-right"
                style={{ fontFamily: 'Outfit' }}
              />
            </div>
          </div>

          {/* Forma de pagamento — only for entradas */}
          {showForma && (
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1.5">Forma de Pagamento</label>
              <div className="grid grid-cols-5 gap-1.5" data-testid="forma-pagamento-selector">
                {Object.entries(FORMA_CONFIG).map(([key, fp]) => (
                  <button key={key} type="button" data-testid={`btn-forma-${key}`}
                    onClick={() => setFormaPagamento(key)}
                    className={`flex flex-col items-center gap-1 py-2 px-1 rounded-xl text-[10px] font-medium transition-all ${
                      formaPagamento === key ? 'ring-2 ring-[#1B4332] bg-[#F0FDF4] text-[#1B4332]' : 'border border-[#E4E4E7] text-[#71717A] hover:bg-[#F4F4F5]'
                    }`}>
                    {key === 'dinheiro' ? <Money size={16} weight={formaPagamento === key ? 'fill' : 'regular'} /> :
                     key === 'pix' ? <PixLogo size={16} weight={formaPagamento === key ? 'fill' : 'regular'} /> :
                     key === 'debito' || key === 'credito' ? <CreditCard size={16} weight={formaPagamento === key ? 'fill' : 'regular'} /> :
                     <DotsThree size={16} weight={formaPagamento === key ? 'fill' : 'regular'} />}
                    {fp.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Origem — for saidas and suprimentos */}
          {showOrigem && (
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1.5">Origem do Recurso</label>
              <div className="grid grid-cols-2 gap-1.5" data-testid="origem-selector">
                {Object.entries(ORIGEM_CONFIG).map(([key, og]) => {
                  const OgIcon = origemIcons[key] || DotsThree;
                  return (
                    <button key={key} type="button" data-testid={`btn-origem-${key}`}
                      onClick={() => setOrigem(key)}
                      className={`flex items-center gap-2 py-2.5 px-3 rounded-xl text-xs font-medium transition-all ${
                        origem === key ? 'ring-2 shadow-sm text-white' : 'border border-[#E4E4E7] hover:bg-[#F4F4F5]'
                      }`}
                      style={origem === key ? { backgroundColor: og.color, ringColor: og.color } : { color: og.color }}>
                      <OgIcon size={16} weight={origem === key ? 'fill' : 'regular'} />
                      {og.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Descricao</label>
            <input data-testid="input-descricao" type="text" value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder={tipo === 'entrada' ? 'Corte + Escova, Produto...' : tipo === 'sangria' ? 'Retirada para cofre' : tipo === 'suprimento' ? 'Aporte Gilson, Troco...' : 'Compra de material...'}
              className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
            />
          </div>

          {observacoes !== undefined && (
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Observacoes</label>
              <input data-testid="input-observacoes" type="text" value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)} placeholder="Opcional"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">Cancelar</button>
            <button type="submit" disabled={saving || !valor} data-testid="btn-confirmar-movimentacao"
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:shadow-lg"
              style={{ backgroundColor: cfg.color }}>
              {saving ? <Spinner size={16} className="animate-spin" /> : <FloppyDisk size={16} weight="bold" />}
              {saving ? 'Salvando...' : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== TURNOS (Caixa 1, Caixa 2 ... e Dia inteiro) ====================
const hhmm = (iso) => { try { return iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }) : ''; } catch { return ''; } };
const CaixaTurnos = ({ data, caixaSel, setCaixaSel, podeAbrir, onAbrir }) => {
  const caixas = data?.caixas || [];
  if (!caixas.length) return null;
  const atual = caixaSel || data?.dia?.id;
  const Btn = ({ id, children, testId }) => (
    <button onClick={() => setCaixaSel(id)} data-testid={testId}
      className={`min-h-[44px] sm:min-h-0 px-3 py-2 rounded-xl text-xs border transition-all text-left ${atual === id || (id === 'dia' && caixaSel === 'dia') ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'bg-white text-[#52525B] border-[#E4E4E7]'}`}>
      {children}
    </button>
  );
  return (
    <section className="mb-4 flex flex-wrap gap-2" data-testid="caixa-turnos">
      {caixas.map(c => (
        <Btn key={c.id} id={c.id} testId={`turno-${c.numero}`}>
          <b>Caixa {c.numero}</b> · {c.aberto_por || c.responsavel || '—'} · {hhmm(c.opened_at)}{c.closed_at ? `–${hhmm(c.closed_at)}` : ''} · {c.status}
          {typeof c.diferenca === 'number' && <span className="ml-1">· dif. {formatCurrency(c.diferenca)}</span>}
        </Btn>
      ))}
      {caixas.length > 1 && <Btn id="dia" testId="turno-dia"><b>Dia inteiro</b> · {formatCurrency(data?.resumo_dia?.total_entradas || 0)}</Btn>}
      {podeAbrir && (
        <button onClick={onAbrir} data-testid="btn-abrir-novo-caixa" className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-xl text-xs font-medium text-white bg-[#1B4332]">
          + Abrir Caixa {caixas.length + 1}
        </button>
      )}
    </section>
  );
};

// ==================== FECHAR CAIXA MODAL ====================
const FecharCaixaModal = ({ selectedDate, resumo, caixaId, onClose, onSuccess }) => {
  const [valorContado, setValorContado] = useState('');
  const [responsavel, setResponsavel] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API}/caixa/fechar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: selectedDate, responsavel, observacoes, caixa_id: caixaId || '',
          valor_contado: valorContado === '' ? null : parseFloat(String(valorContado).replace(',', '.')) }),
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err?.detail?.message || err?.detail || 'Erro ao fechar');
        return;
      }
      onSuccess();
    } catch { /* */ } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="fechar-caixa-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#F4F4F5]">
              <Lock size={20} weight="bold" className="text-[#71717A]" />
            </div>
            <h2 className="text-lg font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>Fechar Caixa</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center text-[#71717A] hover:bg-[#F4F4F5]">
            <X size={20} weight="bold" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 rounded-xl border border-[#E4E4E7] space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#71717A]">Saldo Inicial</span>
              <span className="font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.saldo_inicial)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#1B4332]">+ Entradas</span>
              <span className="font-medium text-[#1B4332]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.total_entradas)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#DC2626]">- Saidas</span>
              <span className="font-medium text-[#DC2626]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.total_saidas)}</span>
            </div>
            {!!resumo.ajustes_pos_fechamento && (
              <div className="flex justify-between text-sm" data-testid="fechar-ajustes-pos-fechamento">
                <span className="text-[#8A6D1F]">Ajustes após o fechamento</span>
                <span className="font-medium text-[#DC2626]" style={{ fontFamily: 'Outfit' }}>- {formatCurrency(resumo.ajustes_pos_fechamento)}</span>
              </div>
            )}
            <div className="pt-2 mt-2 border-t border-[#E4E4E7] flex justify-between">
              <span className="text-sm font-semibold text-[#18181B]">Saldo Final</span>
              <span className="text-lg font-bold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.saldo_atual)}</span>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Dinheiro contado na gaveta (opcional)</label>
              <input data-testid="input-valor-contado" inputMode="decimal" value={valorContado} onChange={(e) => setValorContado(e.target.value)}
                placeholder={`Esperado: ${formatCurrency(resumo?.dinheiro_gaveta_esperado || 0)}`}
                className="w-full mb-3 px-3 py-2 border border-[#E4E4E7] rounded-xl text-sm" />
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Responsavel pelo fechamento</label>
              <input data-testid="input-responsavel-fechamento" type="text" value={responsavel}
                onChange={(e) => setResponsavel(e.target.value)} placeholder="Nome"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#71717A] uppercase tracking-wide mb-1">Observacoes do fechamento</label>
              <input data-testid="input-obs-fechamento" type="text" value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)} placeholder="Opcional"
                className="w-full px-3 py-2.5 bg-[#F7F6F4] border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-[#E4E4E7] rounded-xl text-sm font-medium text-[#71717A] hover:bg-[#F4F4F5]">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="btn-confirmar-fechamento"
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60 bg-[#71717A] transition-all hover:shadow-lg">
                {saving ? <Spinner size={16} className="animate-spin" /> : <Lock size={16} weight="bold" />}
                {saving ? 'Fechando...' : 'Confirmar Fechamento'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==================== ORIGEM BADGE ====================
const OrigemBadge = ({ origem }) => {
  if (!origem) return null;
  const og = ORIGEM_CONFIG[origem];
  if (!og) return null;
  return (
    <span data-testid={`origem-badge-${origem}`}
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded"
      style={{ backgroundColor: og.bg, color: og.color }}>
      {origem === 'caixa_fisico' ? <Storefront size={10} weight="regular" /> :
       origem === 'caixinha_cora' ? <Bank size={10} weight="regular" /> :
       origem === 'aporte_pessoal' ? <HandCoins size={10} weight="regular" /> :
       <DotsThree size={10} weight="regular" />}
      {og.label}
    </span>
  );
};

// ==================== FORMA ICON ====================
const FormaIcon = ({ forma, size = 14 }) => {
  switch (forma) {
    case 'dinheiro': return <Money size={size} weight="regular" />;
    case 'pix': return <PixLogo size={size} weight="regular" />;
    case 'debito':
    case 'credito': return <CreditCard size={size} weight="regular" />;
    default: return <DotsThree size={size} weight="regular" />;
  }
};

// ==================== EXPORT BUTTONS ====================
const ExportButtons = ({ selectedDate }) => {
  const handleExport = async (format) => {
    const { downloadWithAuth } = await import('@/lib/apiClient');
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    await downloadWithAuth(
      `${API}/caixa/export/${format}/${selectedDate}`,
      `caixa_${selectedDate}.${ext}`
    );
  };

  return (
    <div className="flex items-center gap-2" data-testid="export-buttons">
      <button data-testid="btn-export-excel" onClick={() => handleExport('excel')}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#1B4332] transition-all">
        <FileXls size={14} weight="bold" />Excel
      </button>
      <button data-testid="btn-export-pdf" onClick={() => handleExport('pdf')}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-white hover:text-[#DC2626] transition-all">
        <FilePdf size={14} weight="bold" />PDF
      </button>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
export const CaixaDia = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAbrir, setShowAbrir] = useState(false);
  const [showFechar, setShowFechar] = useState(false);
  const [caixaSel, setCaixaSel] = useState('');
  const [receberBandeira, setReceberBandeira] = useState('');
  const [movModal, setMovModal] = useState(null);
  const [retroModal, setRetroModal] = useState(null); // 'abrir' | 'movimentacao' | 'fechar' | null
  const [retroMovTipo, setRetroMovTipo] = useState(null); // tipo for retroactive movimentacao
  const [pendentes, setPendentes] = useState([]);
  const [receberForma, setReceberForma] = useState(null); // { comanda, show }


  const fetchCaixa = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/caixa/dia/${selectedDate}${caixaSel ? `?caixa=${caixaSel}` : ''}`);
      const d = await res.json();
      setData(d);
    } catch { setData(null); } finally { setLoading(false); }
  }, [selectedDate, caixaSel]);
  useEffect(() => { setCaixaSel(''); }, [selectedDate]);

  useEffect(() => { fetchCaixa(); }, [fetchCaixa]);

  // Fetch comandas pendentes for the selected date
  useEffect(() => {
    fetch(`${API}/comandas/pendentes/${selectedDate}`).then(r => r.json()).then(setPendentes).catch(() => setPendentes([]));
  }, [selectedDate, data]); // re-fetch when caixa data changes (after receber)

  const isPastDate = (() => {
    try {
      const target = new Date(selectedDate + 'T12:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      target.setHours(0, 0, 0, 0);
      return target < today;
    } catch { return false; }
  })();

  const changeDate = (delta) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const handleAcaoComanda = async (c, corrigir = false) => {
    await acaoComandaEstorno(API, c, corrigir);
    fetchCaixa();
  };

  const handleDeleteMov = async (id) => {
    if (!window.confirm('Excluir esta movimentacao?')) return;
    const res = await fetch(`${API}/caixa/movimentacao/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const err = await res.json();
      alert(err?.detail?.message || err?.detail || 'Erro');
      return;
    }
    fetchCaixa();
  };

  // Retroactive handlers — now use real user authentication
  const handleRetroAbrir = async (login, password, justificativa, adminUser) => {
    try {
      const res = await fetch(`${API}/caixa/abrir/retroativo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_login: login,
          admin_password: password,
          justificativa,
          data: selectedDate,
          saldo_inicial: 0,
          responsavel: adminUser?.user_name || '',
        }),
      });
      const text = await res.text();
      if (!res.ok) {
        let msg = 'Erro ao autorizar abertura retroativa';
        try { const parsed = JSON.parse(text); msg = parsed?.detail || msg; } catch {}
        alert(msg);
        return;
      }
      setRetroModal(null);
      fetchCaixa();
    } catch (err) {
      alert('Erro de conexao: ' + err.message);
    }
  };

  const handleRetroMov = async (login, password, justificativa, adminUser) => {
    setRetroModal(null);
    setMovModal(retroMovTipo || 'entrada');
    window._retroAuth = { admin_login: login, admin_password: password, justificativa };
  };

  const handleRetroFechar = async (login, password, justificativa, adminUser) => {
    try {
      const res = await fetch(`${API}/caixa/fechar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: selectedDate, responsavel: adminUser?.user_name || 'ADMIN', observacoes: justificativa, caixa_id: dia?.id || '',
          admin_login: login, admin_password: password, justificativa }),
      });
      const text = await res.text();
      if (!res.ok) {
        let msg = 'Erro ao fechar caixa';
        try { const parsed = JSON.parse(text); msg = parsed?.detail?.message || parsed?.detail || msg; } catch {}
        alert(msg);
        return;
      }
      setRetroModal(null);
      fetchCaixa();
    } catch (err) {
      alert('Erro de conexao: ' + err.message);
    }
  };

  const handleReceberComanda = async (comanda, formaPagamento) => {
    try {
      // 1. Create caixa entry linked to comanda
      const itensStr = (comanda.itens || []).map(i => i.nome).filter(Boolean).join(', ');
      const descricao = `${comanda.codigo} — ${comanda.cliente_nome}${itensStr ? ' — ' + itensStr : (comanda.servico ? ' — ' + comanda.servico : '')}`;
      const movBody = { data: selectedDate, tipo: 'entrada', valor: comanda.total, forma_pagamento: formaPagamento, descricao,
        comanda_id: comanda.id, bandeira: FORMAS_CARTAO.includes(formaPagamento) ? receberBandeira : '' };

      // Use retroactive if past date
      let movUrl = `${API}/caixa/movimentacao`;
      if (isPastDate) {
        // Past date: route uses /movimentacao/retroativo; the logged ADMIN's
        // Bearer token authorizes it (apiClient attaches it automatically).
        movUrl = `${API}/caixa/movimentacao/retroativo`;
        movBody.justificativa = `Recebimento comanda ${comanda.codigo} retroativo`;
      }

      const movRes = await fetch(movUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(movBody) });
      if (!movRes.ok) { const t = await movRes.text(); let m; try { m = JSON.parse(t)?.detail; } catch {} alert(m?.message || m || 'Erro ao registrar entrada'); return; }

      // 2. Mark comanda as paid
      await fetch(`${API}/comandas/${comanda.id}/receber`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comanda_id: comanda.id, forma_pagamento: formaPagamento }),
      });

      setReceberForma(null);
      fetchCaixa();
    } catch (err) { alert('Erro: ' + err.message); }
  };


  const dia = data?.dia || { status: 'nao_aberto' };
  const movs = data?.movimentacoes || [];
  const resumo = data?.resumo || { saldo_inicial: 0, total_entradas: 0, total_saidas: 0, saldo_atual: 0, por_forma_pagamento: {}, por_origem: {} };
  const todayFlag = isToday(selectedDate);
  const stCfg = STATUS_CAIXA[dia.status] || STATUS_CAIXA.nao_aberto;

  const isAberto = dia.status === 'aberto';
  const isFechado = dia.status === 'fechado';
  const isNaoAberto = dia.status === 'nao_aberto';

  const canOperate = isAberto && !isPastDate;
  const formas = resumo.por_forma_pagamento || {};
  const origens = resumo.por_origem || {};

  // Historico importado (leitura em paralelo). Null quando o dia nao tem historico.
  const { historico } = useHistoricoDoDia(selectedDate);
  const hasHistorico = !!historico;

  // Aviso ao abrir caixa retroativo em dia com historico: risco de contar em dobro.
  const openRetroModal = (modal) => {
    if (modal === 'abrir' && hasHistorico) {
      const ok = window.confirm(
        'Este dia ja possui movimento no historico importado. ' +
        'Abrir caixa aqui pode duplicar lancamentos deste dia. Deseja continuar?'
      );
      if (!ok) return;
    }
    setRetroModal(modal);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="caixa-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <header className="salonup-header animate-fade-in">
          <div className="salonup-brand">
            <div className="salonup-logo-mark"><Scissors size={22} weight="bold" /></div>
            <span className="salonup-logo-text">Salon<span>Up</span></span>
          </div>
          <button onClick={() => navigate('/gestao')} data-testid="btn-back" className="salonup-btn-back">
            <ArrowLeft size={18} weight="bold" /><span>Voltar para Gestao</span>
          </button>
        </header>

        {/* Breadcrumb */}
        <div className="mb-4 animate-fade-in">
          <p className="salonup-text-xs">
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/')}>Home</span>
            <span className="mx-2">&middot;</span>
            <span className="cursor-pointer hover:text-[#1B4332]" onClick={() => navigate('/gestao')}>Gestao</span>
            <span className="mx-2">&middot;</span>
            <span className="text-[#18181B] font-medium">Caixa</span>
          </p>
        </div>

        {/* Title + Status */}
        <section className="mb-6 animate-fade-in-up">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433215' }}>
                <Wallet size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="salonup-heading-lg">Caixa</h1>
                  <span className="text-[10px] font-semibold px-2.5 py-1 rounded-lg uppercase tracking-wide"
                    style={{ backgroundColor: stCfg.bg, color: stCfg.color }}>
                    {stCfg.label}
                  </span>
                </div>
                <p className="salonup-text-sm mt-1">Movimentacao financeira do dia</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {dia.responsavel && (
                <div className="text-right">
                  <p className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide">Responsavel</p>
                  <p className="text-sm font-medium text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{dia.responsavel}</p>
                </div>
              )}
              {/* Export buttons on closed or any non-empty day */}
              {(isFechado || movs.length > 0) && <ExportButtons selectedDate={selectedDate} />}
            </div>
          </div>
        </section>

        <CaixaTurnos data={data} caixaSel={caixaSel} setCaixaSel={setCaixaSel}
          podeAbrir={!!data?.pode_abrir_novo && (data?.caixas || []).length > 0} onAbrir={() => (isPastDate ? openRetroModal('abrir') : setShowAbrir(true))} />

        {/* Date Navigation */}
        <section className="mb-5 animate-fade-in-up">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button data-testid="btn-prev-day" onClick={() => changeDate(-1)}
                className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <CaretLeft size={16} weight="bold" />
              </button>
              <div className="text-center min-w-0 flex-1 sm:flex-none sm:min-w-[240px]">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-lg font-semibold text-[#18181B] capitalize" style={{ fontFamily: 'Outfit' }} data-testid="current-date">
                    {formatDateBR(selectedDate)}
                  </h2>
                  {isPastDate && <Lock size={14} weight="fill" style={{ color: '#C9A962' }} />}
                </div>
                {todayFlag && <span className="text-[10px] font-semibold text-[#1B4332] uppercase tracking-wide">Hoje</span>}
              </div>
              <button data-testid="btn-next-day" onClick={() => changeDate(1)}
                className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center border border-[#E4E4E7] text-[#71717A] hover:bg-white transition-all">
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {!todayFlag && (
                <button data-testid="btn-hoje" onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  className="shrink-0 min-h-[44px] sm:min-h-0 text-xs font-medium text-[#1B4332] hover:underline mr-2">Ir para hoje</button>
              )}
              <input data-testid="date-picker" type="date" value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="min-w-0 flex-1 sm:flex-none min-h-[44px] sm:min-h-0 px-3 py-2 bg-white border border-[#E4E4E7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4332]/20"
              />
            </div>
          </div>
        </section>

        {/* Past date banner with retroactive action buttons */}
        {isPastDate && !loading && (
          <div data-testid="past-date-banner"
            className="mb-5 p-4 rounded-2xl border border-dashed animate-fade-in"
            style={{ borderColor: '#C9A96240', backgroundColor: '#C9A96208' }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock size={18} weight="fill" style={{ color: '#C9A962' }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: '#8B7340' }}>Data encerrada</p>
                  <p className="text-xs text-[#A1A1AA]">Operacoes normais bloqueadas. Use autorizacao gerencial para ajustes.</p>
                </div>
              </div>
              {/* Retroactive action buttons based on caixa state */}
              <div className="flex items-center gap-2">
                {isAberto && (
                  <>
                    <button data-testid="btn-mov-retroativa" onClick={() => { setRetroMovTipo('entrada'); setRetroModal('movimentacao'); }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white transition-all hover:shadow-lg"
                      style={{ backgroundColor: '#C9A962' }}>
                      <ShieldCheck size={14} weight="bold" />Autorizar lancamento
                    </button>
                    <button data-testid="btn-fechar-retroativo" onClick={() => setRetroModal('fechar')}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all hover:shadow-sm"
                      style={{ borderColor: '#C9A96240', color: '#C9A962', backgroundColor: '#C9A96210' }}>
                      <ShieldCheck size={14} weight="bold" />Liberar fechamento
                    </button>
                  </>
                )}
                {isFechado && (
                  <button data-testid="btn-mov-retroativa-fechado" onClick={() => { setRetroMovTipo('entrada'); setRetroModal('movimentacao'); }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white transition-all hover:shadow-lg"
                    style={{ backgroundColor: '#C9A962' }}>
                    <ShieldCheck size={14} weight="bold" />Autorizar lancamento retroativo
                  </button>
                )}
                {isNaoAberto && (
                  <button data-testid="btn-abrir-retroativo-banner" onClick={() => openRetroModal('abrir')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white transition-all hover:shadow-lg"
                    style={{ backgroundColor: '#C9A962' }}>
                    <ShieldCheck size={14} weight="bold" />Liberar com senha master
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#1B4332] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isNaoAberto && !isPastDate ? (
          hasHistorico ? (
            <HistoricoDiaPanel data={historico} selectedDate={selectedDate} variant="full" />
          ) : (
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: '#1B433210' }}>
              <Wallet size={32} weight="light" style={{ color: '#1B4332' }} />
            </div>
            <h3 className="text-xl font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>Caixa nao aberto</h3>
            <p className="text-sm text-[#71717A] mb-6">Abra o caixa para comecar a registrar movimentacoes do dia.</p>
            <button data-testid="btn-abrir-caixa" onClick={() => setShowAbrir(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
              style={{ backgroundColor: '#1B4332' }}>
              <LockOpen size={18} weight="bold" />Abrir Caixa
            </button>
          </div>
          )
        ) : isNaoAberto && isPastDate ? (
          hasHistorico ? (
            <HistoricoDiaPanel data={historico} selectedDate={selectedDate} variant="full" />
          ) : (
          /* PAST DATE + NOT OPENED + NO HISTORICO → show retroactive open option */
          <div className="salonup-card p-10 text-center max-w-md mx-auto animate-fade-in-up">
            <Lock size={36} weight="light" className="mx-auto mb-4" style={{ color: '#C9A962' }} />
            <h3 className="text-lg font-semibold text-[#18181B] mb-2" style={{ fontFamily: 'Outfit' }}>Caixa nao foi aberto</h3>
            <p className="text-sm text-[#71717A] mb-2">Nenhum registro de caixa para este dia.</p>
            <p className="text-xs text-[#A1A1AA] mb-6">Abertura em data passada requer autorizacao gerencial.</p>
            <button data-testid="btn-abrir-retroativo" onClick={() => openRetroModal('abrir')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
              style={{ backgroundColor: '#C9A962' }}>
              <ShieldCheck size={16} weight="bold" />
              Liberar com senha master
            </button>
          </div>
          )
        ) : (
          <>
            {/* Summary Cards */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 animate-fade-in-up" data-testid="resumo-cards">
              <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="card-saldo-inicial">
                <div className="flex items-center gap-2 mb-3">
                  <Wallet size={16} weight="regular" className="text-[#A1A1AA]" />
                  <span className="text-[10px] font-medium text-[#A1A1AA] uppercase tracking-wide">Saldo Inicial</span>
                </div>
                <p className="text-2xl font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.saldo_inicial)}</p>
              </div>
              <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="card-entradas">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowDown size={16} weight="bold" className="text-[#1B4332]" />
                  <span className="text-[10px] font-medium text-[#1B4332] uppercase tracking-wide">Entradas</span>
                </div>
                <p className="text-2xl font-semibold text-[#1B4332]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.total_entradas)}</p>
              </div>
              <div className="salonup-card p-5 cursor-default hover:transform-none" data-testid="card-saidas">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowUp size={16} weight="bold" className="text-[#DC2626]" />
                  <span className="text-[10px] font-medium text-[#DC2626] uppercase tracking-wide">Saidas</span>
                </div>
                <p className="text-2xl font-semibold text-[#DC2626]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(resumo.total_saidas)}</p>
              </div>
              <div className="salonup-card p-5 cursor-default hover:transform-none border-2" data-testid="card-saldo-atual"
                style={{ borderColor: resumo.saldo_atual >= 0 ? '#1B433230' : '#DC262630' }}>
                <div className="flex items-center gap-2 mb-3">
                  <CurrencyDollar size={16} weight="bold" className="text-[#18181B]" />
                  <span className="text-[10px] font-medium text-[#18181B] uppercase tracking-wide">Saldo Atual</span>
                </div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: resumo.saldo_atual >= 0 ? '#1B4332' : '#DC2626' }}>
                  {formatCurrency(resumo.saldo_atual)}
                </p>
              </div>
            </section>

            {!!resumo.ajustes_pos_fechamento && (
              <div className="mb-6 -mt-3 px-4 py-2.5 rounded-xl border flex items-center justify-between gap-3 text-sm" data-testid="linha-ajustes-pos-fechamento"
                style={{ borderColor: '#C9A96240', backgroundColor: '#C9A96210' }}>
                <span className="text-[#8A6D1F]">Ajustes após o fechamento</span>
                <span className="font-semibold text-[#DC2626]" style={{ fontFamily: 'Outfit' }} data-testid="valor-ajustes-pos-fechamento">- {formatCurrency(resumo.ajustes_pos_fechamento)}</span>
              </div>
            )}

            {/* Quick Actions */}
            {canOperate && (
              <section className="mb-6 animate-fade-in-up" data-testid="acoes-rapidas">
                <div className="flex flex-wrap gap-2">
                  <button data-testid="btn-entrada" onClick={() => setMovModal('entrada')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
                    style={{ backgroundColor: TIPO_CONFIG.entrada.color }}>
                    <ArrowDown size={16} weight="bold" />Entrada
                  </button>
                  <button data-testid="btn-saida" onClick={() => setMovModal('saida')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:shadow-lg"
                    style={{ backgroundColor: TIPO_CONFIG.saida.color }}>
                    <ArrowUp size={16} weight="bold" />Saida
                  </button>
                  <button data-testid="btn-sangria" onClick={() => setMovModal('sangria')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all hover:shadow-sm"
                    style={{ borderColor: TIPO_CONFIG.sangria.color + '40', color: TIPO_CONFIG.sangria.color, backgroundColor: TIPO_CONFIG.sangria.bg }}>
                    <ArrowUp size={16} weight="bold" />Sangria
                  </button>
                  <button data-testid="btn-suprimento" onClick={() => setMovModal('suprimento')}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all hover:shadow-sm"
                    style={{ borderColor: TIPO_CONFIG.suprimento.color + '40', color: TIPO_CONFIG.suprimento.color, backgroundColor: TIPO_CONFIG.suprimento.bg }}>
                    <ArrowDown size={16} weight="bold" />Suprimento
                  </button>
                  <div className="flex-1" />
                  <button data-testid="btn-fechar-caixa" onClick={() => setShowFechar(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#E4E4E7] text-[#71717A] hover:bg-[#F4F4F5] transition-all">
                    <Lock size={16} weight="bold" />Fechar Caixa
                  </button>
                </div>
              </section>
            )}


            {/* Comandas Pendentes para Recebimento */}
            {pendentes.length > 0 && (isAberto || isFechado) && (
              <section className="mb-6 animate-fade-in-up" data-testid="comandas-pendentes">
                <p className="text-[10px] font-semibold text-[#0369A1] uppercase tracking-wider mb-3">
                  Comandas Pendentes para Recebimento ({pendentes.length})
                </p>
                <div className="space-y-2">
                  {pendentes.map(c => (
                    <div key={c.id} data-testid={`pendente-${c.codigo}`}
                      className="salonup-card p-4 cursor-default hover:transform-none flex flex-wrap items-center gap-3 sm:gap-4 border-l-4" style={{ borderLeftColor: '#0369A1' }}>
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F0F9FF' }}>
                        <Receipt size={16} weight="bold" style={{ color: '#0369A1' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{c.codigo}</span>
                          {c.profissional_nome && <span className="text-[10px] text-[#71717A]">{c.profissional_nome}</span>}
                        </div>
                        <p className="text-sm text-[#18181B] truncate">{c.cliente_nome}</p>
                        <p className="text-[11px] text-[#A1A1AA] truncate">
                          {(c.itens && c.itens.length > 0)
                            ? `${c.itens.length} ${c.itens.length === 1 ? 'item' : 'itens'}: ${c.itens.map(i => i.nome).filter(Boolean).join(', ')}`
                            : (c.servico ? `${c.servico}${c.produto ? ' + ' + c.produto : ''}` : '—')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 mr-2">
                        <p className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: '#0369A1' }}>{formatCurrency(c.total)}</p>
                      </div>
                      {(canOperate || (isPastDate && isAberto)) && !receberForma && (
                        <button data-testid={`btn-receber-${c.codigo}`} onClick={() => setReceberForma({ comanda: c, forma: 'dinheiro' })}
                          className="px-3 py-2 rounded-xl text-xs font-medium text-white flex items-center gap-1.5 flex-shrink-0" style={{ backgroundColor: '#1B4332' }}>
                          <CurrencyDollar size={14} weight="bold" />Receber
                        </button>
                      )}
                      {!receberForma && (isAberto || isFechado) && (
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button data-testid={`btn-caixa-corrigir-${c.codigo}`} onClick={() => handleAcaoComanda(c, true)}
                            className="px-2.5 py-2 rounded-xl text-xs font-medium border border-[#C9A96260] text-[#8A6D1F] hover:bg-[#C9A96210]">Corrigir</button>
                          <button data-testid={`btn-caixa-cancelar-${c.codigo}`} onClick={() => handleAcaoComanda(c)}
                            className="px-2.5 py-2 rounded-xl text-xs font-medium border border-red-200 text-[#DC2626] hover:bg-red-50">Cancelar</button>
                        </div>
                      )}
                      {receberForma?.comanda?.id === c.id && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <select value={receberBandeira} onChange={e => setReceberBandeira(e.target.value)} data-testid={`sel-bandeira-${c.codigo}`}
                            className="px-1.5 py-1.5 rounded-lg text-[10px] border border-[#E4E4E7] text-[#71717A]" title="Bandeira (cartão)">
                            <option value="">Bandeira</option>
                            {BANDEIRAS.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                          {['dinheiro','pix','debito','credito','pix_online','cartao_online'].map(fp => (
                            <button key={fp} data-testid={`btn-fp-${fp}-${c.codigo}`}
                              onClick={() => handleReceberComanda(c, fp)}
                              className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium border transition-all ${receberForma.forma === fp ? 'bg-[#1B4332] text-white border-[#1B4332]' : 'border-[#E4E4E7] text-[#71717A] hover:bg-[#F4F4F5]'}`}>
                              {({ dinheiro: 'Din.', pix: 'PIX', debito: 'Deb.', credito: 'Cred.', pix_online: 'Pix online', cartao_online: 'Cartão online' })[fp]}
                            </button>
                          ))}
                          <button onClick={() => setReceberForma(null)} className="text-[#A1A1AA] hover:text-[#71717A]"><X size={14} weight="bold" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Payment Method + Origin Breakdown */}
            {(Object.keys(formas).length > 0 || Object.keys(origens).length > 0) && (
              <section className="mb-6 animate-fade-in-up">
                {Object.keys(formas).length > 0 && (
                  <div className="mb-4">
                    <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Entradas por Forma de Pagamento</p>
                    <div className="flex flex-wrap gap-2" data-testid="formas-breakdown">
                      {Object.entries(formas).map(([fp, val]) => (
                        <div key={fp} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-xl" data-testid={`forma-${fp}`}>
                          <FormaIcon forma={fp} size={16} />
                          <span className="text-xs text-[#71717A]">{FORMA_CONFIG[fp]?.label || fp}</span>
                          <span className="text-sm font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(val)}</span>
                          {Object.entries((resumo.por_bandeira || {})[fp] || {}).map(([b, v]) => (
                            <span key={b} className="text-[10px] text-[#A1A1AA]" data-testid={`bandeira-${fp}-${b}`}>{b} {formatCurrency(v)}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {Object.keys(origens).length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-2">Saidas/Suprimentos por Origem</p>
                    <div className="flex flex-wrap gap-2" data-testid="origens-breakdown">
                      {Object.entries(origens).map(([og, val]) => {
                        const ogCfg = ORIGEM_CONFIG[og] || ORIGEM_CONFIG.outro;
                        return (
                          <div key={og} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-xl" data-testid={`origem-total-${og}`}>
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ogCfg.color }} />
                            <span className="text-xs text-[#71717A]">{ogCfg.label}</span>
                            <span className="text-sm font-semibold text-[#18181B]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(val)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Movimentacoes List */}
            <section className="mb-6 animate-fade-in-up" data-testid="movimentacoes-list">
              <p className="text-[10px] font-semibold text-[#A1A1AA] uppercase tracking-wider mb-3">
                Movimentacoes do Dia ({movs.length})
              </p>
              {movs.length === 0 ? (
                <div className="salonup-card p-8 text-center cursor-default hover:transform-none">
                  <Receipt size={28} weight="light" className="mx-auto mb-3 text-[#A1A1AA]" />
                  <p className="text-sm text-[#71717A]">Nenhuma movimentacao registrada</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {movs.map(m => {
                    const tc = TIPO_CONFIG[m.tipo] || TIPO_CONFIG.entrada;
                    const isPositive = m.tipo === 'entrada' || m.tipo === 'suprimento';
                    return (
                      <div key={m.id} data-testid={`mov-${m.id}`}
                        className="salonup-card p-4 cursor-default hover:transform-none flex flex-wrap items-center gap-3 sm:gap-4 group">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: tc.bg }}>
                          {isPositive ? <ArrowDown size={16} weight="bold" style={{ color: tc.color }} /> :
                           <ArrowUp size={16} weight="bold" style={{ color: tc.color }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
                              style={{ backgroundColor: tc.bg, color: tc.color }}>
                              {tc.label}
                            </span>
                            {m.tipo === 'entrada' && (
                              <span className="flex items-center gap-1 text-[10px] text-[#A1A1AA]">
                                <FormaIcon forma={m.forma_pagamento} size={10} />
                                {FORMA_CONFIG[m.forma_pagamento]?.label || m.forma_pagamento}
                              </span>
                            )}
                            {(m.tipo === 'saida' || m.tipo === 'suprimento') && m.origem && <OrigemBadge origem={m.origem} />}
                            {m.retroativo && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide"
                                style={{ backgroundColor: '#C9A96215', color: '#C9A962' }}>
                                <ClockCounterClockwise size={10} weight="bold" />Ajuste
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#18181B] truncate">{m.descricao || '-'}</p>
                          {m.observacoes && <p className="text-[11px] text-[#A1A1AA] truncate">{m.observacoes}</p>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-lg font-semibold" style={{ fontFamily: 'Outfit', color: isPositive ? '#1B4332' : '#DC2626' }}>
                            {tc.sign} {formatCurrency(m.valor)}
                          </p>
                          <p className="text-[10px] text-[#A1A1AA]">{formatTime(m.created_at)}</p>
                        </div>
                        {m.tipo === 'entrada' && m.comanda_id && !m.estornado && m.origem !== 'estorno' && (
                          <div className="flex gap-1.5 flex-shrink-0">
                            <button data-testid={`btn-caixa-corrigir-mov-${m.id}`} onClick={() => handleAcaoComanda({ id: m.comanda_id, codigo: (m.descricao || '').split(' — ')[0], status: 'paga' }, true)}
                              className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-[#C9A96260] text-[#8A6D1F] hover:bg-[#C9A96210]">Corrigir</button>
                            <button data-testid={`btn-caixa-cancelar-mov-${m.id}`} onClick={() => handleAcaoComanda({ id: m.comanda_id, codigo: (m.descricao || '').split(' — ')[0], status: 'paga' })}
                              className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-red-200 text-[#DC2626] hover:bg-red-50">Cancelar</button>
                          </div>
                        )}
                        {m.estornado && (
                          <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-red-50 text-[#DC2626] flex-shrink-0" data-testid={`mov-estornado-${m.id}`}>Estornado</span>
                        )}
                        {canOperate && (
                          <button data-testid={`btn-del-mov-${m.id}`} onClick={() => handleDeleteMov(m.id)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-[#A1A1AA] hover:bg-red-50 hover:text-red-500 transition-all flex-shrink-0 opacity-0 group-hover:opacity-100">
                            <Trash size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Closed state info */}
            {isFechado && (
              <section className="mb-6 animate-fade-in-up">
                <div className="p-4 rounded-2xl border border-[#E4E4E7] bg-white flex items-center gap-3" data-testid="fechamento-info">
                  <Lock size={16} weight="fill" className="text-[#71717A] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#18181B]">Caixa fechado</p>
                    <p className="text-xs text-[#A1A1AA]">
                      {dia.responsavel_fechamento && `por ${dia.responsavel_fechamento}`}
                      {dia.closed_at && ` em ${formatTime(dia.closed_at)}`}
                      {dia.observacoes_fechamento && ` — ${dia.observacoes_fechamento}`}
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* Historico importado — secao separada abaixo do caixa nativo */}
            {hasHistorico && (
              <HistoricoDiaPanel data={historico} selectedDate={selectedDate} variant="secao" />
            )}
          </>
        )}

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in">
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salao moderno</p>
          <p className="salonup-text-xs">Gestao (CORE) &middot; Caixa</p>
        </footer>
      </div>

      {/* Modals */}
      {showAbrir && (
        <AbrirCaixaModal selectedDate={selectedDate} onClose={() => setShowAbrir(false)} onSuccess={() => { setShowAbrir(false); fetchCaixa(); }} />
      )}
      {showFechar && (
        <FecharCaixaModal selectedDate={selectedDate} resumo={resumo} caixaId={dia.id} onClose={() => setShowFechar(false)} onSuccess={() => { setShowFechar(false); fetchCaixa(); }} />
      )}
      {movModal && (
        <MovimentacaoModal tipo={movModal} selectedDate={selectedDate} onClose={() => { setMovModal(null); window._retroAuth = null; }} onSuccess={() => { setMovModal(null); window._retroAuth = null; fetchCaixa(); }} />
      )}
      {retroModal === 'abrir' && (
        <RetroativoAuthModal
          titulo="Abertura Retroativa"
          descricao="Abrir caixa em data passada"
          selectedDate={selectedDate}
          onClose={() => setRetroModal(null)}
          onAuthorized={handleRetroAbrir}
        />
      )}
      {retroModal === 'movimentacao' && (
        <RetroativoAuthModal
          titulo="Lancamento Retroativo"
          descricao="Registrar movimentacao em data passada"
          selectedDate={selectedDate}
          onClose={() => setRetroModal(null)}
          onAuthorized={handleRetroMov}
        />
      )}
      {retroModal === 'fechar' && (
        <RetroativoAuthModal
          titulo="Fechamento Retroativo"
          descricao="Fechar caixa em data passada"
          selectedDate={selectedDate}
          onClose={() => setRetroModal(null)}
          onAuthorized={handleRetroFechar}
        />
      )}
    </div>
  );
};

export default CaixaDia;
// Cache bust 1776990836
